import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import { ChevronDown, ChevronRight, Search, X } from "lucide-react";
import FacetManager from "./FacetManager";
import ClientSearchCombobox, { ClientOption } from "./ClientSearchCombobox";

interface AdminCategory {
  code: string;
  name: string;
  root: string;
  parent: string;
  rank: number;
}

type Mode = "public" | "restricted" | "hidden";
type EffectiveMode = Mode | "inherit";

interface VisibilityRule {
  category_code: string;
  mode: Mode;
  clients?: string[];
}

interface TreeNode {
  cat: AdminCategory;
  children: TreeNode[];
}

interface Effective {
  mode: Mode; // served state: public | restricted | hidden (never "inherit")
  source: string | null; // ancestor code that decided it, null = own rule or default
}

const MODE_LABEL: Record<EffectiveMode, string> = {
  public: "Público",
  restricted: "Restrito",
  hidden: "Oculto",
  inherit: "Herdado",
};

const MODE_CLASS: Record<EffectiveMode, string> = {
  public: "bg-green-100 text-green-700",
  restricted: "bg-amber-100 text-amber-700",
  hidden: "bg-red-100 text-red-700",
  inherit: "bg-gray-100 text-gray-600",
};

const RANK_LABEL: Record<number, string> = {
  1: "Departamento",
  2: "Família",
  3: "Subfamília",
};

const trimmed = (s: string) => (s || "").trim();

function buildTree(cats: AdminCategory[]): TreeNode[] {
  // ERP codes are hierarchical by prefix (222 / 222CAB / 222MBAAK7DIV), but the
  // `parent` link often points at a code that is not in the table (or at
  // itself); fall back to the longest existing prefix so subfamilies nest.
  const clean = cats.map((c) => ({
    ...c,
    code: trimmed(c.code),
    parent: trimmed(c.parent),
  }));
  const byCode = new Map(clean.map((c) => [c.code, c]));
  const nodes = new Map<string, TreeNode>();
  for (const c of clean) nodes.set(c.code, { cat: c, children: [] });
  const roots: TreeNode[] = [];
  for (const c of clean) {
    let parentCode = "";
    if (c.parent && c.parent !== c.code && byCode.has(c.parent)) {
      parentCode = c.parent;
    } else {
      for (let i = c.code.length - 1; i >= 1; i--) {
        const p = c.code.slice(0, i);
        if (byCode.has(p)) {
          parentCode = p;
          break;
        }
      }
    }
    const node = nodes.get(c.code)!;
    if (parentCode && parentCode !== c.code) {
      nodes.get(parentCode)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

// Effective served visibility per category: walk the parent chain to the nearest
// rule; with no rule anywhere the category is hidden (opt-in default).
function computeEffective(
  cats: AdminCategory[],
  rules: Record<string, VisibilityRule>,
): Map<string, Effective> {
  const byCode = new Map(cats.map((c) => [trimmed(c.code), c]));
  const eff = new Map<string, Effective>();
  const resolve = (code: string): Effective => {
    const cached = eff.get(code);
    if (cached) return cached;
    const rule = rules[code];
    if (rule) {
      const v: Effective = { mode: rule.mode, source: null };
      eff.set(code, v);
      return v;
    }
    const c = byCode.get(code);
    const parent = c ? trimmed(c.parent) : "";
    if (parent && parent !== code && byCode.has(parent)) {
      const p = resolve(parent);
      const v: Effective = { mode: p.mode, source: p.source ?? parent };
      eff.set(code, v);
      return v;
    }
    const v: Effective = { mode: "hidden", source: null };
    eff.set(code, v);
    return v;
  };
  for (const c of cats) resolve(trimmed(c.code));
  return eff;
}

function nodeMatches(node: TreeNode, q: string): boolean {
  if (!q) return true;
  if (
    node.cat.code.toLowerCase().includes(q) ||
    node.cat.name.toLowerCase().includes(q)
  ) {
    return true;
  }
  return node.children.some((c) => nodeMatches(c, q));
}

interface NodeProps {
  node: TreeNode;
  depth: number;
  q: string;
  expanded: Set<string>;
  toggle: (code: string) => void;
  selected: string | null;
  onSelect: (code: string) => void;
  rules: Record<string, VisibilityRule>;
  effective: Map<string, Effective>;
}

const CategoryNode: React.FC<NodeProps> = ({
  node,
  depth,
  q,
  expanded,
  toggle,
  selected,
  onSelect,
  rules,
  effective,
}) => {
  if (!nodeMatches(node, q)) return null;
  const code = node.cat.code;
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(code) || (!!q && hasChildren);
  const eff = effective.get(code);
  const ownRule = rules[code];
  const badge: EffectiveMode = eff ? eff.mode : "hidden";
  const isSelected = selected === code;

  return (
    <div>
      <div
        role="treeitem"
        aria-level={depth + 1}
        aria-expanded={hasChildren ? isOpen : undefined}
        aria-selected={isSelected}
        tabIndex={-1}
        className={
          "group flex items-center gap-1 pr-2 py-1.5 cursor-pointer " +
          "hover:bg-gray-50 focus-within:bg-blue-50" +
          (isSelected
            ? " bg-blue-50 border-l-2 border-blue-500"
            : " border-l-2 border-transparent")
        }
        style={{ paddingLeft: `${depth * 16 + 6}px` }}
      >
        <button
          type="button"
          className="text-gray-400 w-5 h-5 flex items-center justify-center shrink-0 rounded hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) toggle(code);
          }}
          tabIndex={hasChildren ? 0 : -1}
          aria-label={
            hasChildren ? (isOpen ? "Colapsar" : "Expandir") : undefined
          }
        >
          {hasChildren ? (
            isOpen ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )
          ) : null}
        </button>
        <button
          type="button"
          className="flex-1 text-left min-w-0 flex items-center gap-2"
          onClick={() => onSelect(code)}
        >
          <span className="min-w-0">
            <span className="block text-sm font-medium truncate">
              {node.cat.name}
            </span>
            <span className="block text-xs text-gray-500 truncate">
              {code}
              {RANK_LABEL[node.cat.rank]
                ? ` · ${RANK_LABEL[node.cat.rank]}`
                : ""}
              {hasChildren ? ` · ${node.children.length}` : ""}
            </span>
          </span>
        </button>
        <span
          className={
            "text-xs px-2 py-0.5 rounded-full shrink-0 w-20 text-center " +
            MODE_CLASS[badge]
          }
          title={
            ownRule
              ? "Regra própria"
              : eff && eff.source
                ? `Herdado de ${eff.source}`
                : "Oculto por defeito (sem regra)"
          }
        >
          {MODE_LABEL[badge]}
        </span>
      </div>
      {isOpen && hasChildren && (
        <div
          className="border-l border-gray-200"
          style={{ marginLeft: `${depth * 16 + 16}px` }}
        >
          {node.children.map((child) => (
            <CategoryNode
              key={child.cat.code}
              node={child}
              depth={depth + 1}
              q={q}
              expanded={expanded}
              toggle={toggle}
              selected={selected}
              onSelect={onSelect}
              rules={rules}
              effective={effective}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Backoffice tab: category tree (with the EFFECTIVE visibility per node), a rule
 * editor, and the facet manager for the selected category.
 */
const CategoryVisibilityManager: React.FC = () => {
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [rules, setRules] = useState<Record<string, VisibilityRule>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [mode, setMode] = useState<EffectiveMode>("inherit");
  const [clients, setClients] = useState<string[]>([]);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`${API_URLS.CATALOG_ADMIN}/categories`);
      const cats: AdminCategory[] = Array.isArray(res.data?.categories)
        ? res.data.categories
        : [];
      setCategories(cats);
      const map: Record<string, VisibilityRule> = {};
      for (const r of res.data?.rules || []) map[trimmed(r.category_code)] = r;
      setRules(map);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao carregar categorias.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The restricted-client allowlist uses the same cached ERP client directory
  // as the campaigns reseller picker (b2b_user -> {erp_ref, name}).
  useEffect(() => {
    let alive = true;
    setClientsLoading(true);
    api
      .get(`${API_URLS.CAMPAIGNS}/_values/resellers`)
      .then((r) => {
        if (alive) setClientOptions(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (alive) setClientOptions([]);
      })
      .finally(() => {
        if (alive) setClientsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const tree = useMemo(() => buildTree(categories), [categories]);
  const effective = useMemo(
    () => computeEffective(categories, rules),
    [categories, rules],
  );

  // Expand the roots once the tree is available.
  useEffect(() => {
    if (tree.length > 0) setExpanded(new Set(tree.map((n) => n.cat.code)));
  }, [tree.length]);

  const toggle = useCallback((code: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpanded(new Set(categories.map((c) => trimmed(c.code))));
  }, [categories]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  const selectCategory = useCallback(
    (code: string) => {
      setSelected(code);
      setSuccess(null);
      setError(null);
      const rule = rules[code];
      setMode(rule ? rule.mode : "inherit");
      setClients(rule?.clients || []);
    },
    [rules],
  );

  const addClient = (ref: string) => {
    if (!ref) return;
    setClients((prev) => (prev.includes(ref) ? prev : [...prev, ref]));
  };

  const save = async () => {
    if (!selected) return;
    if (mode === "restricted" && clients.length === 0) {
      setError("Modo Restrito exige pelo menos um cliente ERP.");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (mode === "inherit") {
        await api.delete(
          `${API_URLS.CATALOG_ADMIN}/visibility/${encodeURIComponent(selected)}`,
        );
      } else {
        await api.put(
          `${API_URLS.CATALOG_ADMIN}/visibility/${encodeURIComponent(selected)}`,
          { mode, erp_clients: mode === "restricted" ? clients : [] },
        );
      }
      await load();
      // Best-effort: purge the storefront's cached category tree so the
      // slug→ERP-code map (/catalogo/<slug>) reflects the new visibility now,
      // instead of 404ing until the 1h cache expires. A purge failure must not
      // fail the save — the rule is already persisted.
      let purged = false;
      try {
        const res = await fetch("/api/internal/revalidate-storefront", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ tags: ["categories"] }),
        });
        purged = res.ok;
      } catch {
        /* ignored — best-effort cache purge */
      }
      setSuccess(
        purged ? "Regra guardada. Cache da loja renovada." : "Regra guardada.",
      );
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao guardar regra.");
    } finally {
      setSaving(false);
    }
  };

  // trim both sides: the API may pad codes, the tree stores them trimmed.
  const selectedCategory = useMemo(
    () => categories.find((c) => trimmed(c.code) === selected),
    [categories, selected],
  );
  const selectedEff = selectedCategory
    ? effective.get(trimmed(selectedCategory.code))
    : undefined;
  const ancestorBlocking =
    selectedEff && selectedEff.mode !== "public" && mode === "public";

  if (loading) return <div className="text-gray-500 py-6">A carregar...</div>;

  return (
    <div>
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-2 rounded mb-4 text-sm flex items-center justify-between gap-2">
          <span>{error}</span>
          <button className="button is-small" onClick={load}>
            Tentar novamente
          </button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 text-green-700 px-4 py-2 rounded mb-4 text-sm">
          {success}
        </div>
      )}

      <p className="text-gray-500 text-sm mb-4">
        <b>Opt-in:</b> nada é público até ser publicado. Uma regra é herdada
        pela subárvore; uma regra mais específica num descendente sobrepõe-se.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(20rem,2fr)_minmax(0,3fr)] gap-6">
        {/* Category tree */}
        <div>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input pl-9 w-full"
              placeholder="Pesquisar por código ou nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>{categories.length} categorias</span>
            <div className="flex gap-2">
              <button className="hover:text-gray-800" onClick={expandAll}>
                Expandir tudo
              </button>
              <button className="hover:text-gray-800" onClick={collapseAll}>
                Colapsar tudo
              </button>
            </div>
          </div>
          <div
            className="max-h-[32rem] overflow-y-auto border rounded"
            role="tree"
          >
            {tree.length === 0 ? (
              <p className="text-gray-500 text-sm p-3">
                {search
                  ? `Sem resultados para "${search}".`
                  : "Nenhuma categoria."}
              </p>
            ) : (
              tree.map((node) => (
                <CategoryNode
                  key={node.cat.code}
                  node={node}
                  depth={0}
                  q={search.trim().toLowerCase()}
                  expanded={expanded}
                  toggle={toggle}
                  selected={selected}
                  onSelect={selectCategory}
                  rules={rules}
                  effective={effective}
                />
              ))
            )}
          </div>
        </div>

        {/* Detail */}
        <div className="border rounded p-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
          {!selectedCategory ? (
            <p className="text-gray-500 text-sm">
              Selecione uma categoria para gerir a visibilidade e os facets. Sem
              regra, a categoria fica <b>oculta</b>.
            </p>
          ) : (
            <>
              <h3 className="text-base font-semibold text-gray-800">
                {selectedCategory.name}
              </h3>
              <p className="text-xs text-gray-500 mb-1">
                {selectedCategory.code} ·{" "}
                {RANK_LABEL[selectedCategory.rank] ||
                  `rank ${selectedCategory.rank}`}
              </p>
              {selectedEff && (
                <p className="text-xs text-gray-500 mb-4">
                  Efetivo:{" "}
                  <span
                    className={
                      "px-1.5 py-0.5 rounded " + MODE_CLASS[selectedEff.mode]
                    }
                  >
                    {MODE_LABEL[selectedEff.mode]}
                  </span>
                  {selectedEff.source
                    ? ` (herdado de ${selectedEff.source})`
                    : rules[trimmed(selectedCategory.code)]
                      ? " (regra própria)"
                      : " (sem regra em nenhum ascendente)"}
                </p>
              )}

              <div className="mb-4">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Visibilidade
                </span>
                <div className="flex flex-wrap gap-3">
                  {(["inherit", "public", "restricted", "hidden"] as const).map(
                    (m) => (
                      <label
                        key={m}
                        className="flex items-center gap-1 text-sm cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="visibility-mode"
                          checked={mode === m}
                          onChange={() => setMode(m)}
                        />
                        {MODE_LABEL[m]}
                      </label>
                    ),
                  )}
                </div>
                {mode === "inherit" && (
                  <p className="text-xs text-gray-500 mt-1">
                    Remove a regra própria; passa a herdar do ascendente mais
                    próximo
                    {selectedEff && selectedEff.source
                      ? ` (${selectedEff.source} → ${MODE_LABEL[selectedEff.mode]})`
                      : " (ou fica oculta, se nenhum ascendente tiver regra)"}
                    .
                  </p>
                )}
                {ancestorBlocking && (
                  <p className="text-xs text-amber-700 mt-1">
                    Atenção: um ascendente impede a publicação — mesmo sendo
                    Público, este ramo não será servido enquanto o ascendente
                    não for público.
                  </p>
                )}
              </div>

              {mode === "restricted" && (
                <div className="mb-4">
                  <span className="block text-sm font-medium text-gray-700 mb-2">
                    Clientes B2B com acesso
                  </span>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {clients.map((cl) => {
                      const opt = clientOptions.find(
                        (o) => o.erp_ref.toLowerCase() === cl.toLowerCase(),
                      );
                      return (
                        <span
                          key={cl}
                          className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full"
                          title={cl}
                        >
                          {opt?.name ? `${opt.name} (${cl})` : cl}
                          <button
                            onClick={() =>
                              setClients(clients.filter((x) => x !== cl))
                            }
                            aria-label={`Remover ${cl}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                    {clients.length === 0 && (
                      <span className="text-xs text-gray-400">
                        Nenhum cliente selecionado.
                      </span>
                    )}
                  </div>
                  <ClientSearchCombobox
                    id="restricted-client"
                    options={clientOptions}
                    onAdd={addClient}
                    loading={clientsLoading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Acesso por cliente é informativo: a identidade do cliente
                    ainda não é verificada no gateway.
                  </p>
                </div>
              )}

              <button
                className="button is-primary is-small"
                onClick={save}
                disabled={saving}
              >
                {saving ? "A guardar..." : "Guardar regra"}
              </button>

              <div className="mt-5 border-t pt-4">
                <FacetManager
                  key={selectedCategory.code}
                  categoryCode={selectedCategory.code}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryVisibilityManager;
